import { motion } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { TrainingReferenceLink, StaticCard } from '../components/BorderCard';

const ProductInDetailPage = () => {
  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/what" label="Back" />

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Training Module
          </h2>
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-[#1A1A1A] mb-8">
            Product In Detail
          </h1>
          
          <TrainingReferenceLink 
            href="https://canva.link/ztq7ezbqs5ku6lj"
            label="Product In Detail Reference Link"
          />
        </motion.div>

        {/* Overview */}
        <FadeIn delay={0.3}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-10 md:p-12 mb-8">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D32F2F] mb-6 text-center">
              Overview
            </h3>
            <p className="text-base text-[#1A1A1A]/80 leading-relaxed max-w-3xl mx-auto text-center">
              Understanding our products is key to delivering exceptional customer experiences. 
              This module provides a comprehensive deep-dive into The Bear House product range, 
              covering materials, construction, fits, and the unique selling points that set our 
              brand apart in the contemporary menswear market.
            </p>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-8 text-center">
              For reference only
            </div>
          </div>
        </FadeIn>

        {/* Fabric & Materials */}
        <FadeIn delay={0.4}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-8 md:p-10 mb-6">
            <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-4">
              Fabric & Materials
            </h3>
            <ul className="space-y-3 text-sm text-[#1A1A1A]/70 leading-relaxed">
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Premium Cotton:</strong> Breathable, soft-touch fabrics ideal for shirts and casual wear. Know the GSM and weave types.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Blended Fabrics:</strong> Cotton-polyester and cotton-linen blends for durability and comfort.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Care Instructions:</strong> Always communicate washing and maintenance guidelines to ensure product longevity.</span>
              </li>
            </ul>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-6">
              For reference only
            </div>
          </div>
        </FadeIn>

        {/* Fit Guide */}
        <FadeIn delay={0.5}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-8 md:p-10 mb-6">
            <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-4">
              Fit Guide
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#F8F6F2] border border-[rgba(26,26,26,0.1)] flex items-center justify-center">
                  <span className="text-sm font-light text-[#1A1A1A]">S</span>
                </div>
                <h4 className="text-xs uppercase tracking-[0.1em] font-semibold text-[#1A1A1A] mb-1">Slim Fit</h4>
                <p className="text-xs text-[#1A1A1A]/60">Tailored silhouette, close to body</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#F8F6F2] border border-[rgba(26,26,26,0.1)] flex items-center justify-center">
                  <span className="text-sm font-light text-[#1A1A1A]">R</span>
                </div>
                <h4 className="text-xs uppercase tracking-[0.1em] font-semibold text-[#1A1A1A] mb-1">Regular Fit</h4>
                <p className="text-xs text-[#1A1A1A]/60">Classic cut, comfortable room</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#F8F6F2] border border-[rgba(26,26,26,0.1)] flex items-center justify-center">
                  <span className="text-sm font-light text-[#1A1A1A]">O</span>
                </div>
                <h4 className="text-xs uppercase tracking-[0.1em] font-semibold text-[#1A1A1A] mb-1">Oversized</h4>
                <p className="text-xs text-[#1A1A1A]/60">Relaxed, contemporary streetwear</p>
              </div>
            </div>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-6 text-center">
              For reference only
            </div>
          </div>
        </FadeIn>

        {/* Key Selling Points */}
        <FadeIn delay={0.6}>
          <div className="bg-white rounded-lg border border-[rgba(26,26,26,0.08)] p-8 md:p-10">
            <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1A1A1A] mb-4">
              Key Selling Points
            </h3>
            <ul className="space-y-3 text-sm text-[#1A1A1A]/70 leading-relaxed">
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Versatility:</strong> Pieces that transition from casual to semi-formal effortlessly.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Quality Craftsmanship:</strong> Attention to detail in stitching, buttons, and finishes.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Modern Aesthetic:</strong> Contemporary designs that resonate with the style-conscious man.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#D32F2F] mt-1">•</span>
                <span><strong>Value Proposition:</strong> Premium quality at accessible price points.</span>
              </li>
            </ul>
            <div className="text-[9px] uppercase tracking-wider text-[#1A1A1A]/30 mt-6">
              For reference only
            </div>
          </div>
        </FadeIn>
      </div>
    </PageTransition>
  );
};

export default ProductInDetailPage;
